// Card #1652 (design §6.1/§6.2 type 5) — per-judge percentages, PERCENTAGES
// ONLY, never raw points (design §4.5/§6.2's exposure rule). The upstream
// shape (results.js's buildRideResult -> perJudge) only ever carries
// `percentage`, never a raw-points field, so there is nothing here that
// could accidentally leak one — this component simply never reads
// anything else off a row.
//
// A still-scoring judge's row has no `percentage` key (results.js
// ~421-430's PARTIAL suppression), but scoreboard-public.js ~747-756 passes
// `result.perJudge` through whenever the class has a panel regardless of
// whether the ride is PARTIAL or CONFIRMED — so a row missing `percentage`
// DOES reach this component on a still-scoring ride, it is not filtered out
// upstream. AC3 forbids rendering an omitted field empty (a bare judge
// position with nothing beside it), so a row with no percentage is dropped
// entirely here rather than rendered position-only.
// Card #1694 AC4 — percentage formatting goes through score-common's
// formatPercentage (3 decimals, single shared formatter) rather than a
// local `.toFixed()`.
import { formatPercentage } from "score-common";
import { isScorePubliclyVisible } from "../../util/publicScoreVisibility";
import "./PerJudgeBreakout.css";

// Card #2727 -- PerJudgeBreakout's OWN floor, not a repeat of a caller's.
// Before this card, only SingleEntryLayout's shouldShowPostRideResult gated
// the sub-55 case, and it gated on the COMPOSITE (fields.scoreHero), not
// each individual judge's percentage -- so a class whose composite happened
// to clear 55 but carried an individual judge score below it (exactly the
// #2727 incident: ride 224 judge B at 39.239 against a true 58.804) reached
// this component's old `percentage != null` filter and printed the raw
// per-judge number regardless of the ancestor's composite-level gate. Each
// row is now checked against the same PUBLIC_SCORE_FLOOR individually, so a
// row is dropped both when it has no percentage yet (still scoring, #2727's
// unchanged prior behavior) AND when it has one but it's below the floor.
// Card #3116 -- more judges than fit one row of the post-ride result column
// at 16:9. The column is ~806px wide there and each percentage cell is
// ~245px, so a third judge already sits at the edge and a fourth runs off the
// canvas (5 judges, the system max, ended at x=2569 on a 1920 canvas). From
// this many judges up the list gets the `--many` modifier, which lets it wrap
// onto a second row. At or below the threshold the markup and CSS are
// untouched, so 1-3 judge boards stay byte-identical.
export const PER_JUDGE_BREAKOUT_WRAP_ABOVE = 3;

export default function PerJudgeBreakout({ perJudgeBreakout }) {
  const rows = (Array.isArray(perJudgeBreakout) ? perJudgeBreakout : []).filter(
    (row) => row?.percentage != null && isScorePubliclyVisible(Number(row.percentage))
  );
  if (!rows.length) return null;

  return (
    <ul
      className={
        rows.length > PER_JUDGE_BREAKOUT_WRAP_ABOVE ? "per-judge-breakout per-judge-breakout--many" : "per-judge-breakout"
      }
      data-testid="per-judge-breakout"
    >
      {rows.map((row, index) => (
        <li key={row.judgeId || index} className="per-judge-breakout__row" data-testid="per-judge-breakout-row">
          {row.position ? (
            <span className="per-judge-breakout__position" data-testid="per-judge-breakout-position">
              {row.position}
            </span>
          ) : null}
          <span className="per-judge-breakout__percentage" data-testid="per-judge-breakout-percentage">
            {formatPercentage(Number(row.percentage))}
          </span>
        </li>
      ))}
    </ul>
  );
}
